import React, { ReactNode, useEffect, useState } from 'react';

import { isEmpty, noop } from 'lodash';

import { useChatDocumentContext } from '~/components/Chat/context/ChatDocumentContext.tsx';
import { ClaimDocumentMetadata } from '~/components/Chat/types.ts';
import { MarkdownSection } from '~/core/atoms/MarkdownSection';
import Tags from '~/core/atoms/Tag/Tags';
import { TagData } from '~/core/atoms/Tag/types';
import TypingEffect from '~/core/atoms/TypingEffect';
import DocumentIcon from '~/core/iconComponents/icons/DocumentIcon';
import LoadingDots from '~/core/molecules/LoadingDots/LoadingDots';
import cn from '~/utils/cn';
import { toTimeAgo } from '~/utils/Dates/DateTimeUtils.ts';

interface ChatMessageProps {
  message: string | React.ReactNode;
  avatar?: React.ReactNode;
  sender: string;
  senderRole?: string;
  createdAt?: Date;
  className?: string;
  withTypingEffect?: boolean;
  onEndTypingEffect?: () => void;
  isLoading?: boolean;
  isReferenceTags?: boolean;
  direction?: 'ltr' | 'rtl';
  documentsUsed?: string[];
  claimNumber?: string;
  messagePrefix?: ReactNode;
}

const ALLOWED_REDIRECT = ['textual_file', 'image'];

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  avatar,
  sender,
  senderRole,
  createdAt,
  className,
  withTypingEffect = false,
  onEndTypingEffect = noop,
  isLoading = false,
  isReferenceTags = false,
  direction = 'ltr',
  claimNumber,
  documentsUsed = [],
  messagePrefix,
}) => {
  const [typingEffectCompleted, setTypingEffectCompleted] = useState(false);
  const [documentTags, setDocumentTags] = useState<TagData[]>([]);
  const [timestamp] = useState<Date | undefined>(createdAt);
  const formattedTime = timestamp ? toTimeAgo(timestamp) : null;

  const onTypingEffectStarted = () => setTypingEffectCompleted(false);
  const onTypingEffectComplete = () => {
    setTypingEffectCompleted(true);
    onEndTypingEffect?.();
  };
  const { getDocumentsByIds } = useChatDocumentContext();

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const documentsData = await getDocumentsByIds(documentsUsed);
        setDocumentTags(
          documentsData
            .filter((doc: ClaimDocumentMetadata) => doc.document_type !== 'note')
            .map((doc: ClaimDocumentMetadata) => {
              const shouldRedirectDoc = ALLOWED_REDIRECT.includes(doc.document_type || '');
              return {
                id: doc.id,
                text: doc.document_name || `Document ${doc.id}`, // Fallback to ID if no name
                icon: <DocumentIcon className="h-16 w-16" />,
                disabled: false,
                className: shouldRedirectDoc ? 'cursor-pointer' : '',
                Wrapper: shouldRedirectDoc
                  ? ({ children }: React.PropsWithChildren) => (
                      <a
                        href={`/api/v1/claims/${claimNumber}/documents/${doc.id}/public_url`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {children}
                      </a>
                    )
                  : null,
              };
            })
        );
      } catch (error) {
        console.error('Failed to fetch document metadata:', error);
      }
    };
    if (claimNumber) {
      // noinspection JSIgnoredPromiseFromCall
      fetchDocuments();
    }
  }, [claimNumber, documentsUsed, getDocumentsByIds]);

  return (
    <div
      className={cn('__MESSAGE_CONTAINER__ flex w-full max-w-full gap-12 rounded-md p-16 text-sm', className, {
        'inline-flex w-max flex-row-reverse pl-40': direction === 'rtl',
      })}
    >
      {avatar || <div className="h-32 w-32 flex-shrink-0 gap-12 rounded-full bg-blue-600" />}
      <div
        className={cn('flex w-full flex-col gap-4', {
          'inline-flex w-max': direction === 'rtl',
        })}
      >
        <div
          className={cn('__MESSAGE_HEADER__ flex gap-4 font-bold', {
            'self-end': direction === 'rtl',
          })}
        >
          <div>
            {messagePrefix ? messagePrefix : null}
            {sender}
            {senderRole && ` - ${senderRole}`}
          </div>
          <span className="font-normal text-slate-700">
            <span className="text-sm">{formattedTime ? ` • ${formattedTime}` : ''}</span>
          </span>
        </div>
        <div className="__MESSAGE_CONTENT__ text-sm whitespace-pre-line text-slate-900">
          {isLoading ? (
            <LoadingDots className="flex justify-start p-8" color="bg-pink-700" />
          ) : typeof message === 'string' ? (
            withTypingEffect && !typingEffectCompleted ? (
              <TypingEffect
                strings={[message]}
                onBegin={onTypingEffectStarted}
                onComplete={onTypingEffectComplete}
                typeSpeed={2}
              />
            ) : (
              <MarkdownSection markdown={message} className="p-0" />
            )
          ) : (
            message
          )}
        </div>
        {!isEmpty(documentsUsed) ? (
          <span className={cn({ invisible: isEmpty(documentTags) })}>
            {isReferenceTags ? <div className="mb-4 text-xs font-medium text-slate-700 italic">References:</div> : null}
            <Tags tags={documentTags} shouldDisplayClose={false} className="flex-wrap gap-4" />

            {/*Placeholder to prevent jumps*/}
            {isEmpty(documentTags) ? (
              <Tags tags={[{ id: 0, text: '0' }]} shouldDisplayClose={false} className="flex-wrap gap-4" />
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default ChatMessage;
